import {ContentHotel} from '@findhotel/sapi'
import {PaymentFormValues} from 'components/PaymentForm/initialFormValues'
import {compose, lensPath, over} from 'ramda'
import {Dispatch} from 'redux'
import {hashEmail} from 'utils/hashEmail'
import {urlParams} from 'utils/urlParams'

import {getOrGenerateAnonymousId} from '@vio/core/src/_web/anonymousId'
import {DevicePlatform} from '@vio/core/src/_web/utils/userAgent/types'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  AnalyticsContext,
  Category,
  CheckoutOfferContext,
  Entity,
  Page,
  PriceContext
} from '@vio/core/src/analytics/types/Events'
import {Source} from '@vio/core/src/api-types/bovio/response/booking'
import {StateType} from '@vio/core/src/auth/modules/slice'
import {Brand} from '@vio/core/src/brand/types'
import {getEpochSeconds, getTimezone} from '@vio/core/src/utils/date'

interface TrackBookingSuccess {
  brand: Brand
  bookingRequestId: string
  hotel?: Partial<ContentHotel> | null
  merchantOfRecordCode: string
  offerContext: CheckoutOfferContext | null
  priceContext: PriceContext | null
  dispatch: Dispatch
  user: StateType['user']
  bookingContactDetails?: PaymentFormValues['customer'] | null
  searchId?: string | null
  source?: Source
  isAuthenticated: boolean
  userCountry?: string
  platform: DevicePlatform
  paymentMethod: string | undefined
}

export const trackBookingSuccess = async ({
  brand,
  bookingRequestId,
  merchantOfRecordCode,
  offerContext,
  priceContext,
  dispatch,
  user,
  bookingContactDetails,
  hotel,
  source = Source.Default,
  isAuthenticated,
  userCountry,
  platform,
  paymentMethod
}: TrackBookingSuccess) => {
  const anonymousId = getOrGenerateAnonymousId()
  const {locale, checkIn, checkOut} = urlParams()

  const cio = customerIo(brand)

  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.Booking,
      action: Action.Succeeded,
      page: Page.Payment,
      payload: {
        bookingRequestID: bookingRequestId,
        merchantOfRecord: merchantOfRecordCode,
        platform,
        paymentMethod
      }
    })
  )

  const timezone = getTimezone()

  await cio.addOrUpdateUser({
    anonymousId,
    country: userCountry,
    language: locale,
    user,
    bookingContactDetails,
    timezone
  })

  cio.trackEvent({
    name: `${Category.System}_Direct_${Entity.Booking}_${Action.Succeeded}`,
    pageName: Page.Payment,
    userId: user?.attributes?.sub,
    anonymousId,
    [AnalyticsContext.OfferContext]: over(
      lensPath<CheckoutOfferContext | null>([
        'cancellationPolicy',
        'freeRefundableUntil'
      ]),
      compose(String, getEpochSeconds),
      offerContext
    ),
    [AnalyticsContext.PriceContext]: priceContext,
    timezone,
    hotelName: hotel?.hotelName,
    hotelAddress: hotel?.displayAddress,
    checkIn: Number(getEpochSeconds(checkIn)),
    checkOut: Number(getEpochSeconds(checkOut)),
    source
  })

  // Push hashed email to GTM for Google Enhanced Conversions (non-authenticated checkout only)
  const guestEmail = !isAuthenticated ? bookingContactDetails?.email : null

  if (guestEmail && typeof window !== 'undefined' && window.dataLayer) {
    const sha256Email = await hashEmail(guestEmail)
    window.dataLayer.push({
      event: 'UserEmailHashed',
      sha256Email
    })
  }
}
